import { useState, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';
import { apiUrl } from '../lib/api';

interface User {
	id: string;
	name: string;
	email: string;
	role: string;
	active: boolean;
	createdAt: string;
}

/**
 * User Management Page
 *
 * Allows superadmin to view all users and create new users.
 * Only accessible to users with superadmin role.
 */
export function UsersPage() {
	const { user } = useAuth();
	const [users, setUsers] = useState<User[]>([]);
	const [loading, setLoading] = useState(true);
	const [submitting, setSubmitting] = useState(false);
	const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

	// Form state
	const [username, setUsername] = useState('');
	const [email, setEmail] = useState('');
	const [password, setPassword] = useState('');
	const [role, setRole] = useState<'viewer' | 'editor' | 'admin' | 'superadmin'>('viewer');
	const [updatingUserId, setUpdatingUserId] = useState<string | null>(null);

	// Delete user state
	const [deleteUserId, setDeleteUserId] = useState<string | null>(null);
	const [deleting, setDeleting] = useState(false);

	// Fetch users on mount
	useEffect(() => {
		fetchUsers();
	}, []);

	const fetchUsers = async () => {
		try {
			setLoading(true);
			const response = await fetch(apiUrl('/api/users'), {
				credentials: 'include',
			});

			if (!response.ok) {
				throw new Error('Failed to fetch users');
			}

			const data = await response.json();
			setUsers(data.users || []);
		} catch (error) {
			console.error('Error fetching users:', error);
			showMessage('error', 'Gagal memuat daftar pengguna');
		} finally {
			setLoading(false);
		}
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		setSubmitting(true);
		setMessage(null);

		try {
			// Validate inputs
			if (!username || !email || !password) {
				showMessage('error', 'Semua field harus diisi');
				setSubmitting(false);
				return;
			}

			if (password.length < 8) {
				showMessage('error', 'Password minimal 8 karakter');
				setSubmitting(false);
				return;
			}

			const response = await fetch(apiUrl('/api/users'), {
				method: 'POST',
				credentials: 'include',
				headers: {
					'Content-Type': 'application/json',
				},
				body: JSON.stringify({
					name: username,
					email,
					password,
					role,
				}),
			});

			const data = await response.json();

			if (!response.ok) {
				throw new Error(data.error || 'Failed to create user');
			}

			// Success - clear form and refetch users
			showMessage('success', 'Pengguna berhasil dibuat');
			setUsername('');
			setEmail('');
			setPassword('');
			setRole('viewer');
			await fetchUsers();
		} catch (error: any) {
			console.error('Error creating user:', error);
			showMessage('error', error.message || 'Gagal membuat pengguna');
		} finally {
			setSubmitting(false);
		}
	};

	const showMessage = (type: 'success' | 'error', text: string) => {
		setMessage({ type, text });
		// Auto-hide after 5 seconds
		setTimeout(() => setMessage(null), 5000);
	};

	const updateUser = async (userId: string, updates: { role?: string; active?: boolean }) => {
		try {
			setUpdatingUserId(userId);
			const response = await fetch(apiUrl(`/api/users/${userId}`), {
				method: 'PATCH',
				credentials: 'include',
				headers: {
					'Content-Type': 'application/json',
				},
				body: JSON.stringify(updates),
			});

			const data = await response.json();

			if (!response.ok) {
				throw new Error(data.error || 'Failed to update user');
			}

			showMessage('success', 'Pengguna berhasil diperbarui');
			await fetchUsers();
		} catch (error: any) {
			console.error('Error updating user:', error);
			showMessage('error', error.message || 'Gagal memperbarui pengguna');
		} finally {
			setUpdatingUserId(null);
		}
	};

	const handleRoleChange = (userId: string, newRole: string) => {
		updateUser(userId, { role: newRole });
	};

	const handleToggleActive = (userId: string, currentActive: boolean) => {
		updateUser(userId, { active: !currentActive });
	};

	const handleDeleteUser = async (userId: string) => {
		try {
			setDeleting(true);
			const response = await fetch(apiUrl(`/api/users/${userId}`), {
				method: 'DELETE',
				credentials: 'include',
			});

			const data = await response.json();

			if (!response.ok) {
				throw new Error(data.error || 'Failed to delete user');
			}

			showMessage('success', 'Pengguna berhasil dihapus');
			setDeleteUserId(null);
			await fetchUsers();
		} catch (error: any) {
			console.error('Error deleting user:', error);
			showMessage('error', error.message || 'Gagal menghapus pengguna');
		} finally {
			setDeleting(false);
		}
	};

	const openDeleteDialog = (userId: string) => {
		setDeleteUserId(userId);
	};

	const closeDeleteDialog = () => {
		setDeleteUserId(null);
	};

	// Check if user has access (backup check - route is also protected)
	const isSuperadmin = user?.role === 'superadmin';
	const isAdmin = user?.role === 'admin';
	const canManageUsers = isSuperadmin; // Only superadmin can create/edit/delete

	if (!isSuperadmin && !isAdmin) {
		return (
			<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
				<div className="bg-red-50 border border-red-200 rounded-lg p-6">
					<h2 className="text-xl font-bold text-red-900 mb-2">Akses Ditolak</h2>
					<p className="text-red-700">
						Hanya admin dan superadmin yang dapat mengakses halaman ini.
					</p>
				</div>
			</div>
		);
	}

	return (
		<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
			<div className="mb-6">
				<h1 className="text-2xl font-bold text-gray-900">Kelola Pengguna</h1>
				<p className="text-gray-600 mt-1">Tambah dan kelola pengguna sistem</p>
			</div>

			{message && (
				<div
					className={`mb-6 p-4 rounded-lg ${
						message.type === 'success'
							? 'bg-green-50 border border-green-200'
							: 'bg-red-50 border border-red-200'
					}`}
				>
					<p
						className={`text-sm ${
							message.type === 'success' ? 'text-green-800' : 'text-red-800'
						}`}
					>
						{message.text}
					</p>
				</div>
			)}

			{/* Add User Form - Only visible to superadmin */}
			{canManageUsers && (
			<div className="bg-white shadow rounded-lg p-6 mb-6">
				<h2 className="text-xl font-semibold text-gray-900 mb-4">Tambah Pengguna Baru</h2>
				<form onSubmit={handleSubmit} className="space-y-4">
					<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
						<div>
							<label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-2">
								Nama
							</label>
							<input
								id="username"
								type="text"
								value={username}
								onChange={(e) => setUsername(e.target.value)}
								required
								className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition"
								placeholder="John Doe"
								disabled={submitting}
							/>
						</div>

						<div>
							<label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
								Email
							</label>
							<input
								id="email"
								type="email"
								value={email}
								onChange={(e) => setEmail(e.target.value)}
								required
								className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition"
								placeholder="nama@email.com"
								disabled={submitting}
							/>
						</div>

						<div>
							<label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
								Password
							</label>
							<input
								id="password"
								type="password"
								value={password}
								onChange={(e) => setPassword(e.target.value)}
								required
								minLength={8}
								className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition"
								placeholder="Minimal 8 karakter"
								disabled={submitting}
							/>
						</div>

						<div>
							<label htmlFor="role" className="block text-sm font-medium text-gray-700 mb-2">
								Role
							</label>
							<select
								id="role"
								value={role}
								onChange={(e) => setRole(e.target.value as 'viewer' | 'editor' | 'admin' | 'superadmin')}
								className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition"
								disabled={submitting}
							>
								<option value="viewer">Viewer</option>
								<option value="editor">Editor</option>
								<option value="admin">Admin</option>
								<option value="superadmin">Superadmin</option>
							</select>
						</div>
					</div>

					<div className="flex justify-end">
						<button
							type="submit"
							disabled={submitting}
							className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:ring-4 focus:ring-blue-300 font-medium transition disabled:opacity-50 disabled:cursor-not-allowed"
						>
							{submitting ? 'Memproses...' : 'Tambah Pengguna'}
						</button>
					</div>
				</form>
			</div>
			)}

			{/* User List */}
			<div className="bg-white shadow rounded-lg p-6">
				<h2 className="text-xl font-semibold text-gray-900 mb-4">Daftar Pengguna</h2>

				{loading ? (
					<div className="flex items-center justify-center py-8">
						<div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
					</div>
				) : users.length === 0 ? (
					<div className="text-center py-8 text-gray-500">Belum ada pengguna</div>
				) : (
					<div className="overflow-x-auto">
						<table className="min-w-full divide-y divide-gray-200">
							<thead className="bg-gray-50">
								<tr>
									<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
										Nama
									</th>
									<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
										Email
									</th>
									<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
										Role
									</th>
									<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
										Status
									</th>
									<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
										Tanggal Dibuat
									</th>
									{canManageUsers && (
									<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
										Aksi
									</th>
									)}
								</tr>
							</thead>
							<tbody className="bg-white divide-y divide-gray-200">
								{users.map((userItem) => {
									const isCurrentUser = userItem.id === user?.id;
									const isUpdating = updatingUserId === userItem.id;

									return (
										<tr key={userItem.id} className={!userItem.active ? 'bg-gray-50 opacity-60' : ''}>
											<td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
												{userItem.name}
												{isCurrentUser && (
													<span className="ml-2 text-xs text-blue-600">(Anda)</span>
												)}
											</td>
											<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
												{userItem.email}
											</td>
											<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
												{canManageUsers ? (
												<select
													value={userItem.role}
													onChange={(e) => handleRoleChange(userItem.id, e.target.value)}
													disabled={isCurrentUser || isUpdating}
													className={`px-2 py-1 text-xs font-medium rounded border-0 ${
														userItem.role === 'superadmin'
															? 'bg-purple-100 text-purple-800'
															: userItem.role === 'admin'
																? 'bg-orange-100 text-orange-800'
																: userItem.role === 'editor'
																	? 'bg-blue-100 text-blue-800'
																	: 'bg-gray-100 text-gray-800'
													} ${isCurrentUser ? 'cursor-not-allowed' : 'cursor-pointer'}`}
												>
													<option value="viewer">viewer</option>
													<option value="editor">editor</option>
													<option value="admin">admin</option>
													<option value="superadmin">superadmin</option>
												</select>
												) : (
												<span className={`px-2 py-1 text-xs font-medium rounded ${
													userItem.role === 'superadmin'
														? 'bg-purple-100 text-purple-800'
														: userItem.role === 'admin'
															? 'bg-orange-100 text-orange-800'
															: userItem.role === 'editor'
																? 'bg-blue-100 text-blue-800'
																: 'bg-gray-100 text-gray-800'
												}`}>
													{userItem.role}
												</span>
												)}
											</td>
											<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
												<span
													className={`px-2 py-1 text-xs font-medium rounded ${
														userItem.active
															? 'bg-green-100 text-green-800'
															: 'bg-red-100 text-red-800'
													}`}
												>
													{userItem.active ? 'Aktif' : 'Nonaktif'}
												</span>
											</td>
											<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
												{new Date(userItem.createdAt).toLocaleDateString('id-ID', {
													year: 'numeric',
													month: 'long',
													day: 'numeric',
												})}
											</td>
											{canManageUsers && (
											<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
												<div className="flex space-x-2">
													<button
														onClick={() => handleToggleActive(userItem.id, userItem.active)}
														disabled={isCurrentUser || isUpdating}
														className={`px-3 py-1 text-xs font-medium rounded transition ${
															userItem.active
																? 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'
																: 'bg-green-100 text-green-700 hover:bg-green-200'
														} ${isCurrentUser || isUpdating ? 'opacity-50 cursor-not-allowed' : ''}`}
													>
														{isUpdating ? '...' : userItem.active ? 'Nonaktifkan' : 'Aktifkan'}
													</button>
													<button
														onClick={() => openDeleteDialog(userItem.id)}
														disabled={isCurrentUser || isUpdating}
														className={`px-3 py-1 text-xs font-medium rounded bg-red-100 text-red-700 hover:bg-red-200 transition ${
															isCurrentUser || isUpdating ? 'opacity-50 cursor-not-allowed' : ''
														}`}
													>
														Hapus
													</button>
												</div>
											</td>
											)}
										</tr>
									);
								})}
							</tbody>
						</table>
					</div>
				)}
			</div>

			{/* Delete User Confirmation Modal */}
			{deleteUserId && (
				<div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
					<div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
						<h3 className="text-lg font-semibold text-gray-900 mb-4">
							Hapus Pengguna
						</h3>
						<p className="text-sm text-gray-600 mb-4">
							Apakah Anda yakin ingin menghapus pengguna{' '}
							<span className="font-medium">
								{users.find(u => u.id === deleteUserId)?.name}
							</span>
							? Tindakan ini tidak dapat dibatalkan.
						</p>
						<div className="flex justify-end space-x-3">
							<button
								onClick={closeDeleteDialog}
								disabled={deleting}
								className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition disabled:opacity-50"
							>
								Batal
							</button>
							<button
								onClick={() => handleDeleteUser(deleteUserId)}
								disabled={deleting}
								className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition disabled:opacity-50"
							>
								{deleting ? 'Menghapus...' : 'Hapus'}
							</button>
						</div>
					</div>
				</div>
			)}
		</div>
	);
}
